import { useMemo, useState } from 'react'
import Link from 'next/link'
import { useRouter } from 'next/router'
import fs from 'fs'
import path from 'path'
import Meta from '../components/Meta'
import {
  ArrowRight,
  CalendarDays,
  Download,
  ExternalLink,
  FileText,
  Mail,
  MapPin,
  Search,
  X,
} from 'lucide-react'
import { requestPlanningUpdatesResultsScroll } from '../lib/planning/homepageSearchScroll'

interface ReportEntry {
  filename: string
  title: string
  isoDate: string | null
  prettyDate: string | null
}

interface ReportsProps {
  reports: ReportEntry[]
  generatedAt: string
}

const REPORTS_DIR_PARTS = ['public', 'weekly-planning-reports'] as const

const parseFilename = (filename: string): ReportEntry => {
  const cleanedTitle = filename
    .replace(/_/g, ' ')
    .replace('.pdf', '')
    .replace(/(\d{4})-(\d{2})-(\d{2})\s*/, '')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/\b\w/g, (c) => c.toUpperCase())

  const dateMatch = filename.match(/(\d{4})-(\d{2})-(\d{2})/)
  let isoDate: string | null = null
  let prettyDate: string | null = null

  if (dateMatch) {
    const d = new Date(parseInt(dateMatch[1], 10), parseInt(dateMatch[2], 10) - 1, parseInt(dateMatch[3], 10))
    isoDate = d.toISOString().slice(0, 10)
    prettyDate = d.toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })
  }

  return {
    filename,
    title: cleanedTitle || 'Edinburgh Weekly Planning Report',
    isoDate,
    prettyDate,
  }
}

export async function getStaticProps() {
  const reportsDir = path.join(process.cwd(), ...REPORTS_DIR_PARTS)
  let reports: ReportEntry[] = []

  try {
    reports = fs
      .readdirSync(reportsDir)
      .filter((file) => file.endsWith('.pdf'))
      .sort()
      .reverse()
      .map(parseFilename)
  } catch (error) {
    console.error('Error reading reports directory', error)
  }

  return {
    props: {
      reports,
      generatedAt: new Date().toISOString(),
    },
    revalidate: 3600,
  }
}

const reportHref = (report: ReportEntry) => `/weekly-planning-reports/${encodeURIComponent(report.filename)}`

function formatGeneratedAt(value: string) {
  const parsed = new Date(value)
  if (Number.isNaN(parsed.getTime())) return 'Updated hourly'
  return parsed.toLocaleDateString('en-GB', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  })
}

function nextMondayLabel(value: string) {
  const now = new Date(value)
  if (Number.isNaN(now.getTime())) return 'Monday'
  const day = now.getDay()
  const offset = day === 1 ? 7 : (8 - day) % 7
  const monday = new Date(now)
  monday.setDate(now.getDate() + (offset === 0 ? 7 : offset))
  return monday.toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long' })
}

function ReportSearchPanel() {
  const router = useRouter()
  const [query, setQuery] = useState('')

  const submit = () => {
    const cleaned = query.trim()
    requestPlanningUpdatesResultsScroll()
    router.push(cleaned
      ? { pathname: '/planning-updates', query: { search: cleaned } }
      : { pathname: '/planning-updates' })
  }

  return (
    <section className="border border-[var(--c-border)] border-t-2 border-t-[var(--c-text-primary)] bg-[var(--c-surface)] p-5 sm:p-6">
      <p className="pr-section-label">Search before you subscribe</p>
      <p className="mt-2 text-sm leading-6 text-[var(--c-text-secondary)]">
        Check an address, street, category or reference in live updates, then use the weekly archive when recurring monitoring is useful.
      </p>
      <div className="mt-5 flex flex-col gap-3 sm:flex-row">
        <label className="relative min-w-0 flex-1">
          <Search className="pointer-events-none absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-[var(--c-accent)]" strokeWidth={1.6} />
          <input
            type="search"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === 'Enter') submit()
            }}
            placeholder="Search planning applications..."
            className="h-12 w-full border border-[var(--c-border)] bg-[var(--c-bg)] pl-12 pr-4 text-sm text-[var(--c-text-primary)] outline-none transition placeholder:text-[var(--c-text-tertiary)] focus:border-[var(--c-text-primary)]"
            autoComplete="off"
            spellCheck={false}
          />
        </label>
        <button type="button" className="pr-button h-12 px-5" onClick={submit}>
          Search updates
          <ArrowRight className="h-4 w-4" strokeWidth={1.5} />
        </button>
      </div>
    </section>
  )
}

function LatestReportPanel({
  latest,
  pdfOpen,
  onToggleReader,
}: {
  latest: ReportEntry
  pdfOpen: boolean
  onToggleReader: () => void
}) {
  const href = reportHref(latest)

  return (
    <section id="latest" className="scroll-mt-28">
      <div className="mb-7 flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <p className="pr-section-label">Latest issue</p>
          <h2 className="pr-heading mt-4">The current weekly planning report.</h2>
        </div>
        {latest.prettyDate ? <p className="pr-data uppercase tracking-[0.14em]">Published {latest.prettyDate}</p> : null}
      </div>

      <div className="grid gap-5 lg:grid-cols-[minmax(0,1fr)_minmax(300px,420px)]">
        <article className="pr-panel border-t-2 border-t-[var(--c-text-primary)] p-6 sm:p-7">
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div>
              <p className="pr-kicker pr-kicker--accent">Edinburgh Planning Weekly</p>
              <h3 className="mt-5 max-w-3xl font-['Newsreader'] text-4xl font-medium leading-tight text-[var(--c-text-primary)] sm:text-5xl">
                {latest.title}
              </h3>
            </div>
            <span className="flex h-12 w-12 items-center justify-center border border-[var(--c-border)] bg-[var(--c-bg-secondary)] text-[var(--c-accent)]">
              <FileText className="h-5 w-5" strokeWidth={1.5} />
            </span>
          </div>

          <p className="mt-5 max-w-2xl text-sm leading-6 text-[var(--c-text-secondary)]">
            A compact PDF digest for the week: volume, standout applications, source references, categories and links back into Planning Updates.
          </p>

          <div className="mt-7 flex flex-wrap gap-3">
            <a href={href} target="_blank" rel="noopener noreferrer" className="pr-button">
              Read PDF
              <ExternalLink className="h-4 w-4" strokeWidth={1.5} />
            </a>
            <a href={href} download className="pr-button pr-button--ghost">
              Download
              <Download className="h-4 w-4" strokeWidth={1.5} />
            </a>
            <button type="button" className="pr-button pr-button--ghost" onClick={onToggleReader}>
              {pdfOpen ? 'Hide reader' : 'Inline reader'}
            </button>
          </div>
        </article>

        <aside className="pr-panel p-5">
          <p className="pr-section-label">Report structure</p>
          <div className="mt-5 grid gap-3">
            {[
              ['01', 'Headline view', 'Weekly volume and notable movement.'],
              ['02', 'Top categories', 'The application types shaping updates.'],
              ['03', 'Highlights', 'Standout applications with impact scores.'],
              ['04', 'Full digest', 'Every application grouped for scanning.'],
            ].map(([number, title, text]) => (
              <div key={number} className="grid grid-cols-[2.5rem_minmax(0,1fr)] gap-3 border-t border-[var(--c-border)] pt-3 first:border-t-0 first:pt-0">
                <span className="pr-data text-[var(--c-accent)]">{number}</span>
                <span>
                  <span className="block text-sm font-semibold text-[var(--c-text-primary)]">{title}</span>
                  <span className="mt-1 block text-sm leading-5 text-[var(--c-text-secondary)]">{text}</span>
                </span>
              </div>
            ))}
          </div>
        </aside>
      </div>

      {pdfOpen ? (
        <div className="pr-panel mt-5 overflow-hidden">
          <div className="flex items-center justify-between border-b border-[var(--c-border)] bg-[var(--c-bg-secondary)] px-4 py-3">
            <span className="pr-data uppercase tracking-[0.14em]">Inline PDF reader</span>
            <button type="button" className="pr-textlink" onClick={onToggleReader}>
              <X className="h-4 w-4" strokeWidth={1.5} />
              Close
            </button>
          </div>
          <object data={href} type="application/pdf" className="h-[72vh] min-h-[520px] w-full bg-[var(--c-bg)]">
            <div className="p-5 text-sm text-[var(--c-text-secondary)]">
              The PDF reader is not available in this browser. <a className="pr-textlink" href={href}>Open the report</a>.
            </div>
          </object>
        </div>
      ) : null}
    </section>
  )
}

function ArchiveCard({ report, index }: { report: ReportEntry; index: number }) {
  const href = reportHref(report)

  return (
    <article className="pr-panel flex h-full flex-col p-5 transition-colors hover:border-[var(--c-text-tertiary)] hover:bg-[var(--c-surface-hover)]">
      <div className="flex items-start justify-between gap-4">
        <p className="pr-data uppercase tracking-[0.14em]">{report.prettyDate ?? `Issue ${index + 1}`}</p>
        <span className="flex h-9 w-9 items-center justify-center border border-[var(--c-border)] bg-[var(--c-bg-secondary)] text-[var(--c-accent)]">
          <FileText className="h-4 w-4" strokeWidth={1.5} />
        </span>
      </div>
      <h3 className="mt-5 font-['Newsreader'] text-2xl font-medium leading-tight text-[var(--c-text-primary)]">
        {report.title}
      </h3>
      <div className="mt-6 flex flex-1 items-end gap-3">
        <a href={href} target="_blank" rel="noopener noreferrer" className="pr-textlink">
          Open
          <ExternalLink className="h-4 w-4" strokeWidth={1.5} />
        </a>
        <a href={href} download className="pr-textlink">
          Download
          <Download className="h-4 w-4" strokeWidth={1.5} />
        </a>
      </div>
    </article>
  )
}

const WeeklyPlanningReports: React.FC<ReportsProps> = ({ reports, generatedAt }) => {
  const [pdfOpen, setPdfOpen] = useState(false)
  const latest = reports[0] ?? null

  const generatedLabel = useMemo(() => formatGeneratedAt(generatedAt), [generatedAt])
  const nextDispatch = useMemo(() => nextMondayLabel(generatedAt), [generatedAt])

  return (
    <>
      <Meta
        title="Weekly Planning Reports | City Scope"
        description="Browse the City Scope weekly planning report archive and search live Scottish planning applications across Edinburgh, Midlothian, East Lothian and West Lothian."
        canonical="/weekly-planning-reports"
      />

      <div>
        <main className="planning-register min-h-screen bg-[var(--c-bg)] text-[var(--c-text-primary)]">
          <section className="mx-auto max-w-7xl px-4 pb-12 pt-[calc(var(--navbar-height,64px)+3rem)] sm:px-6 lg:px-8 lg:pt-[calc(var(--navbar-height,64px)+4rem)]">
            <div className="grid gap-8 lg:grid-cols-[minmax(0,1fr)_minmax(280px,420px)] lg:items-end">
              <div>
                <p className="pr-kicker pr-kicker--accent">CityScope / Weekly Reports</p>
                <h1 className="pr-masthead mt-5 max-w-5xl">
                  Weekly <em>planning reports</em>
                </h1>
                <p className="mt-6 max-w-2xl text-base leading-7 text-[var(--c-text-secondary)] sm:text-lg">
                  Search live updates, then use the weekly report archive for a calmer view of the applications that landed across the planning feed.
                </p>
              </div>

              <div className="pr-panel p-5">
                <p className="pr-section-label">Next dispatch</p>
                <dl className="mt-5 grid grid-cols-2 border border-[var(--c-border)] bg-[var(--c-bg-secondary)]">
                  <div className="p-3">
                    <dt className="pr-data text-[10px] uppercase tracking-[0.14em]">Archive</dt>
                    <dd className="pr-numeral mt-1 text-3xl">{reports.length}</dd>
                  </div>
                  <div className="border-l border-[var(--c-border)] p-3">
                    <dt className="pr-data text-[10px] uppercase tracking-[0.14em]">Delivery</dt>
                    <dd className="pr-numeral mt-1 text-3xl">09:00</dd>
                  </div>
                </dl>
                <p className="pr-data mt-4">Next issue: {nextDispatch}</p>
                <p className="pr-data mt-2">Archive refreshed: {generatedLabel}</p>
              </div>
            </div>

            <div className="mt-8">
              <ReportSearchPanel />
            </div>
          </section>

          <section className="border-y border-[var(--c-border)] bg-[var(--c-bg-secondary)]">
            <div className="mx-auto grid max-w-7xl gap-4 px-4 py-5 sm:grid-cols-3 sm:px-6 lg:px-8">
              {[
                { icon: CalendarDays, label: 'Weekly rhythm', text: 'The archive is built around the Monday planning review cadence.' },
                { icon: MapPin, label: 'Council context', text: 'Reports sit beside the map and updates so every item can be checked at source.' },
                { icon: Mail, label: 'Email ready', text: 'Subscribe when you want the digest delivered instead of browsing manually.' },
              ].map(({ icon: Icon, label, text }) => (
                <div key={label} className="flex gap-3">
                  <Icon className="mt-1 h-4 w-4 flex-shrink-0 text-[var(--c-accent)]" strokeWidth={1.6} />
                  <div>
                    <p className="text-sm font-semibold text-[var(--c-text-primary)]">{label}</p>
                    <p className="mt-1 text-sm leading-6 text-[var(--c-text-secondary)]">{text}</p>
                  </div>
                </div>
              ))}
            </div>
          </section>

          <div className="mx-auto max-w-7xl space-y-14 px-4 py-14 sm:px-6 lg:px-8">
            {latest ? (
              <LatestReportPanel
                latest={latest}
                pdfOpen={pdfOpen}
                onToggleReader={() => setPdfOpen((value) => !value)}
              />
            ) : (
              <section className="pr-panel p-6">
                <p className="pr-section-label">Latest issue</p>
                <h2 className="pr-heading mt-4">No weekly reports are available yet.</h2>
                <p className="mt-4 max-w-2xl text-sm leading-6 text-[var(--c-text-secondary)]">
                  The archive will populate when PDF reports are added to the weekly planning reports folder.
                </p>
              </section>
            )}

            <section id="archive" className="scroll-mt-28">
              <div className="mb-7 flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
                <div>
                  <p className="pr-section-label">Archive</p>
                  <h2 className="pr-heading mt-4">Previous weekly reports.</h2>
                </div>
                <p className="pr-data uppercase tracking-[0.14em]">{reports.length} reports</p>
              </div>

              {reports.length > 0 ? (
                <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                  {reports.map((report, index) => (
                    <ArchiveCard key={report.filename} report={report} index={index} />
                  ))}
                </div>
              ) : null}
            </section>

            <section id="subscribe" className="pr-panel grid gap-6 p-6 sm:p-8 lg:grid-cols-[minmax(0,1fr)_auto] lg:items-center">
              <div>
                <p className="pr-section-label">Stay informed</p>
                <h2 className="pr-heading mt-4">Get the weekly planning digest by email.</h2>
                <p className="mt-4 max-w-2xl text-sm leading-6 text-[var(--c-text-secondary)]">
                  Keep live updates for searching, and use the Monday email for the planning changes worth checking each week.
                </p>
              </div>
              <div className="flex flex-wrap gap-3">
                <Link href="/reports/signup" className="pr-button">
                  Subscribe
                  <Mail className="h-4 w-4" strokeWidth={1.5} />
                </Link>
                <Link href="/planning-updates" className="pr-button pr-button--ghost">
                  Browse updates
                  <Search className="h-4 w-4" strokeWidth={1.5} />
                </Link>
              </div>
            </section>
          </div>
        </main>
      </div>
    </>
  )
}

export default WeeklyPlanningReports
